/**
 * Enhanced Keyboard Navigation
 * Improves keyboard accessibility for menus, modals, and interactive elements
 */
(function() {
  'use strict';

  class KeyboardNavigation {
    constructor() {
      this.init();
    }
    
    init() {
      // Skip to main content
      this.setupSkipLinks();
      
      // Arrow key navigation for menus
      this.setupMenuNavigation();
      
      // Modal focus traps
      this.setupFocusTraps();
      
      // Escape key handlers
      this.setupEscapeHandlers();
      
      // Live region for announcements
      this.createLiveRegion();
    }
    
    setupSkipLinks() {
      const skipLink = document.querySelector('.skip-to-content-link');
      if (skipLink) {
        // Handle both click and keyboard activation
        const handleSkip = (e) => {
          e.preventDefault();
          const href = skipLink.getAttribute('href');
          if (!href) return;
          
          // Handle both hash links and direct element selection
          const targetId = href.replace('#', '');
          const target = document.getElementById(targetId) || document.querySelector(href);
          
          if (target) {
            // Ensure target is focusable
            if (!target.hasAttribute('tabindex')) {
              target.setAttribute('tabindex', '-1');
            }
            
            // Focus and scroll to target
            target.focus();
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
            
            // Announce to screen readers
            this.announce('Skipped to main content');
            
            // Remove focus outline after a moment (for visual users)
            setTimeout(() => {
              if (document.activeElement === target) {
                target.blur();
                target.focus(); // Re-focus to show outline briefly
              }
            }, 100);
          }
        };
        
        // Handle click events
        skipLink.addEventListener('click', handleSkip);
        
        // Handle keyboard activation (Enter/Space)
        skipLink.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleSkip(e);
          }
        });
      }
    }
    
    setupMenuNavigation() {
      const menus = document.querySelectorAll('[role="menu"], nav[role="navigation"], .t4s-section-header, .t4s-nav__ul');
      
      menus.forEach(menu => {
        const items = Array.from(menu.querySelectorAll('a, button, [tabindex="0"]')).filter(item => {
          // Exclude hidden items
          return !item.closest('[hidden]') && 
                 !item.hasAttribute('hidden') && 
                 window.getComputedStyle(item).display !== 'none';
        });
        
        items.forEach((item, index) => {
          item.addEventListener('keydown', (e) => {
            let target = null;
            
            switch(e.key) {
              case 'ArrowDown':
              case 'ArrowRight':
                e.preventDefault();
                target = items[index + 1] || items[0];
                break;
              case 'ArrowUp':
              case 'ArrowLeft':
                e.preventDefault();
                target = items[index - 1] || items[items.length - 1];
                break;
              case 'Home':
                e.preventDefault();
                target = items[0];
                break;
              case 'End':
                e.preventDefault();
                target = items[items.length - 1];
                break;
              case 'Escape':
                e.preventDefault();
                const menuContainer = item.closest('[aria-expanded]');
                if (menuContainer) {
                  menuContainer.setAttribute('aria-expanded', 'false');
                  menuContainer.focus();
                }
                // Close any open dropdowns
                const openDropdown = menu.querySelector('[aria-expanded="true"]');
                if (openDropdown) {
                  openDropdown.setAttribute('aria-expanded', 'false');
                  openDropdown.focus();
                }
                break;
            }
            
            if (target) {
              target.focus();
              // If it's a button that controls a menu, open it
              if (target.hasAttribute('aria-expanded') && target.getAttribute('aria-expanded') === 'false') {
                target.click();
              }
            }
          });
        });
        
        // Toggle buttons controlling menus
        menu.querySelectorAll('[data-drawer-open], [aria-controls], .has-submenu > a, [aria-expanded]').forEach((el) => {
          el.addEventListener('click', () => {
            const expanded = el.getAttribute('aria-expanded');
            if (expanded !== null) {
              el.setAttribute('aria-expanded', expanded === 'true' ? 'false' : 'true');
            }
          });
        });
      });
    }
    
    setupFocusTraps() {
      const modals = document.querySelectorAll('[role="dialog"], .t4s-modal, .modal, [data-focus-trap], .t4s-drawer');
      
      modals.forEach(modal => {
        const focusableElements = Array.from(modal.querySelectorAll(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(el => {
          // Exclude hidden elements
          return !el.closest('[hidden]') && 
                 !el.hasAttribute('hidden') && 
                 window.getComputedStyle(el).display !== 'none' &&
                 window.getComputedStyle(el).visibility !== 'hidden';
        });
        
        if (focusableElements.length === 0) return;
        
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        
        const trapHandler = (e) => {
          if (e.key !== 'Tab') return;
          
          // Check if modal is actually visible
          const isVisible = modal.offsetParent !== null && 
                          !modal.hasAttribute('hidden') &&
                          window.getComputedStyle(modal).display !== 'none';
          
          if (!isVisible) return;
          
          if (e.shiftKey) {
            if (document.activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            if (document.activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        };
        
        modal.addEventListener('keydown', trapHandler);
        
        // Focus first element when modal opens
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
              const isHidden = modal.getAttribute('aria-hidden') === 'true';
              if (!isHidden && firstElement) {
                setTimeout(() => firstElement.focus(), 100);
              }
            }
          });
        });
        
        observer.observe(modal, { attributes: true, attributeFilter: ['aria-hidden'] });
        
        // Also check for class changes (for modals that use classes)
        const classObserver = new MutationObserver(() => {
          const isVisible = modal.offsetParent !== null && 
                          !modal.classList.contains('t4s-op-0') &&
                          !modal.classList.contains('hidden');
          if (isVisible && firstElement) {
            setTimeout(() => firstElement.focus(), 100);
          }
        });
        
        classObserver.observe(modal, { attributes: true, attributeFilter: ['class'] });
      });
    }
    
    setupEscapeHandlers() {
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          // Close open modals
          const openModal = document.querySelector('[role="dialog"][aria-hidden="false"], .modal:not([hidden]):not(.hidden)');
          if (openModal) {
            const closeButton = openModal.querySelector('[data-drawer-close], [data-close-modal], [aria-label*="close" i]');
            if (closeButton) {
              closeButton.click();
            } else {
              // Try to close by setting aria-hidden
              openModal.setAttribute('aria-hidden', 'true');
            }
          }
          
          // Close open drawers
          const openDrawer = document.querySelector('.t4s-drawer:not(.t4s-op-0):not([hidden])');
          if (openDrawer) {
            const closeButton = openDrawer.querySelector('[data-drawer-close]');
            if (closeButton) {
              closeButton.click();
            }
          }
          
          // Close open menus
          const openMenu = document.querySelector('[aria-expanded="true"]');
          if (openMenu) {
            openMenu.setAttribute('aria-expanded', 'false');
            openMenu.focus();
          }
        }
      });
    }
    
    createLiveRegion() {
      // Create live region if it doesn't exist
      if (!document.getElementById('aria-live-region')) {
        const liveRegion = document.createElement('div');
        liveRegion.id = 'aria-live-region';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        liveRegion.setAttribute('aria-atomic', 'true');
        liveRegion.className = 'visually-hidden';
        document.body.appendChild(liveRegion);
      }
    }
    
    announce(message) {
      const liveRegion = document.getElementById('aria-live-region');
      if (liveRegion) {
        liveRegion.textContent = message;
        setTimeout(() => {
          liveRegion.textContent = '';
        }, 1000);
      }
    }
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new KeyboardNavigation());
  } else {
    new KeyboardNavigation();
  }
})();

